// ====================================================================================
// Component: DataUploadDrawer
// Slide-in drawer from the right for uploading campaign data files.
// Campaign ID is resolved automatically server-side — not exposed here.
// Polls GET /status/{job_id} until the pipeline completes or fails.
// ====================================================================================
import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2, UploadCloud, CheckCircle2, AlertTriangle, X, Upload } from "lucide-react";

const POLL_INTERVAL_MS  = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s before timeout
const UPLOAD_ENDPOINT   = "https://dante-campaign-data-etl-pipeline-84264019333.europe-west1.run.app";

// ====================================================================================
// DataUploadDrawer
// Props:
//   isOpen   — boolean
//   onClose  — () => void
// ====================================================================================
export default function DataUploadDrawer({ isOpen, onClose }) {
  const [file, setFile]           = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [stage, setStage]         = useState("idle");   // idle | uploading | polling | success | duplicate | error
  const [progress, setProgress]   = useState(0);
  const [result, setResult]       = useState(null);
  const [errorMsg, setErrorMsg]   = useState("");
  const fileInputRef              = useRef(null);
  const pollTimerRef              = useRef(null);

  // Clean up polling on unmount
  useEffect(() => () => clearTimeout(pollTimerRef.current), []);

  // Reset when drawer closes
  const handleClose = useCallback(() => {
    clearTimeout(pollTimerRef.current);
    setFile(null);
    setStage("idle");
    setProgress(0);
    setResult(null);
    setErrorMsg("");
    onClose();
  }, [onClose]);

  // ── File acceptance ────────────────────────────────────────────────────────
  const acceptFile = useCallback((f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["csv", "xls", "xlsx"].includes(ext)) {
      setErrorMsg(`"${f.name}" is not supported. Use .csv, .xls, or .xlsx.`);
      setStage("error");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setErrorMsg(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB — exceeds the 50 MB limit.`);
      setStage("error");
      return;
    }
    setFile(f);
    setStage("idle");
    setErrorMsg("");
  }, []);

  const onDrop      = useCallback((e) => { e.preventDefault(); setIsDragging(false); acceptFile(e.dataTransfer.files[0]); }, [acceptFile]);
  const onDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);

  // ── Polling ────────────────────────────────────────────────────────────────
  const pollStatus = useCallback((jobId, attempts = 0) => {
    if (attempts >= POLL_MAX_ATTEMPTS) {
      setErrorMsg("Processing timed out. Check the audit log or try again.");
      setStage("error");
      return;
    }
    pollTimerRef.current = setTimeout(async () => {
      try {
        const res  = await fetch(`/status/${jobId}`);
        const data = await res.json();

        if (data.status === "processing") {
          setProgress((p) => Math.min(p + 5, 88));
          pollStatus(jobId, attempts + 1);
        } else if (data.status === "success") {
          setProgress(100);
          setResult(data);
          setStage("success");
        } else if (data.status === "duplicate") {
          setStage("duplicate");
          setErrorMsg("This exact file has already been successfully processed for this campaign.");
        } else {
          setErrorMsg(data.error || "An unknown pipeline error occurred.");
          setStage("error");
        }
      } catch {
        setErrorMsg("Lost connection while waiting for pipeline status.");
        setStage("error");
      }
    }, POLL_INTERVAL_MS);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!file) return;

    setStage("uploading");
    setProgress(0);
    setErrorMsg("");

    const formData = new FormData();
    formData.append("file", file);
    // campaign_id is resolved by the server from context — not sent from client

    try {
      const res  = await fetch(UPLOAD_ENDPOINT, { method: "POST", body: formData });
      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(data.detail || `Server error ${res.status}`);
        setStage("error");
        return;
      }

      if (data.job_id) {
        setStage("polling");
        setProgress(14);
        pollStatus(data.job_id);
      } else {
        setErrorMsg("Unexpected response from server.");
        setStage("error");
      }
    } catch {
      setErrorMsg("Could not reach the upload server. Check your connection.");
      setStage("error");
    }
  }, [file, pollStatus]);

  const isProcessing = stage === "uploading" || stage === "polling";
  const canSubmit    = !!file && !isProcessing && stage !== "success";
  const scoreColor   = (s) => s >= 0.9 ? "#1D9E75" : s >= 0.7 ? "#BA7517" : "#E24B4A";

  const drawer = (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* ── Drawer ────────────────────────────────────────────────────────── */}
      <div
        className={`fixed top-0 right-0 z-[9999] h-screen w-full max-w-md bg-slate-900 border-l border-slate-700/60 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-0.5">Campaign Data</p>
            <h2 className="text-lg font-semibold text-white">Upload Data</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {/* Context note */}
          <p className="text-sm text-slate-400">
            Please upload your campaign data file here, this will allow us to process your campaign with more accuracy.
          </p>

          {/* ── Dropzone ────────────────────────────────────────────────── */}
          {stage !== "success" && (
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isProcessing
                  ? "border-slate-700 cursor-not-allowed opacity-50"
                  : isDragging
                  ? "border-blue-500 bg-blue-500/5 cursor-copy"
                  : file
                  ? "border-slate-600 bg-slate-800/40 cursor-pointer hover:border-slate-500"
                  : "border-slate-700 cursor-pointer hover:border-slate-500"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                className="hidden"
                onChange={(e) => acceptFile(e.target.files[0])}
              />
              <UploadCloud
                size={32}
                className={`mx-auto mb-3 ${
                  isDragging ? "text-blue-400" : file ? "text-blue-400" : "text-slate-600"
                }`}
              />
              {file ? (
                <>
                  <p className="text-sm font-medium text-white mb-1">{file.name}</p>
                  <p className="text-xs text-slate-500">
                    {(file.size / 1024).toFixed(0)} KB · click to change
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-white mb-1">Drop your file here</p>
                  <p className="text-xs text-slate-500">.csv · .xlsx · .xls — max 50 MB</p>
                </>
              )}
            </div>
          )}

          {/* ── Processing state ─────────────────────────────────────────── */}
          {isProcessing && (
            <div className="py-2 space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="animate-spin text-blue-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">
                    {stage === "uploading" ? "Uploading..." : "Pipeline running..."}
                  </p>
                  {stage === "polling" && (
                    <p className="text-xs text-slate-500 mt-0.5">Parsing · Cleaning · Staging · Merging</p>
                  )}
                </div>
              </div>
              <div className="bg-slate-800 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* ── Success state ────────────────────────────────────────────── */}
          {stage === "success" && result && (
            <div className="space-y-4">

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-900/40 flex items-center justify-center shrink-0">
                  <CheckCircle2 size={18} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Upload complete</p>
                  <p className="text-xs text-slate-400 mt-0.5">{file?.name}</p>
                </div>
              </div>

              {/* Quality metrics grid */}
              {result.quality && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Rows written",  value: result.rows_processed },
                    { label: "Quality score", value: result.quality.score?.toFixed(2), color: scoreColor(result.quality.score) },
                    { label: "Ward column",   value: result.quality.ward_col_original || "WA22CD" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="bg-slate-800 rounded-lg px-3 py-2.5">
                      <p className="text-xs text-slate-500 mb-1">{label}</p>
                      <p className="text-base font-semibold truncate" style={{ color: color || "white" }}>{value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Ward invalid warning */}
              {result.quality?.ward_invalid_rows > 0 && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg px-3 py-2.5 flex gap-2 items-start">
                  <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-300">
                    {result.quality.ward_invalid_rows} ward code{result.quality.ward_invalid_rows > 1 ? "s" : ""} don't match ONS GSS format — retained but flagged.
                    {result.quality.ward_invalid_samples?.length > 0 && (
                      <span className="block mt-0.5 text-amber-400/70">e.g. {result.quality.ward_invalid_samples[0]}</span>
                    )}
                  </p>
                </div>
              )}

              {/* Schema drift warning */}
              {result.quality?.schema_drift && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg px-3 py-2.5 flex gap-2 items-start">
                  <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-300">{result.quality.schema_drift.message}</p>
                </div>
              )}

              {/* Anomaly warning */}
              {result.quality?.anomalous_rows > 0 && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg px-3 py-2.5 flex gap-2 items-start">
                  <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-300">
                    {result.quality.anomalous_rows} row{result.quality.anomalous_rows > 1 ? "s" : ""} flagged with statistical anomalies.
                  </p>
                </div>
              )}

              {/* Target table */}
              <div className="bg-slate-800 rounded-lg px-3 py-2.5">
                <p className="text-xs text-slate-500 mb-0.5">Written to</p>
                <p className="text-xs text-slate-300 font-mono break-all">{result.target_table}</p>
              </div>

              {/* Upload another */}
              <button
                onClick={() => { setFile(null); setStage("idle"); setResult(null); setProgress(0); }}
                className="w-full text-sm text-slate-400 hover:text-white py-2 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors"
              >
                Upload another file
              </button>
            </div>
          )}

          {/* ── Error / Duplicate state ──────────────────────────────────── */}
          {(stage === "error" || stage === "duplicate") && (
            <div className="bg-red-950/40 border border-red-800/50 rounded-xl px-4 py-3 flex gap-3 items-start">
              <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-300 mb-0.5">
                  {stage === "duplicate" ? "Already processed" : "Upload rejected"}
                </p>
                <p className="text-xs text-red-400/80 leading-relaxed">{errorMsg}</p>
                <button
                  onClick={() => { setStage("idle"); setErrorMsg(""); }}
                  className="mt-2 text-xs text-red-400 hover:text-red-200 underline underline-offset-2"
                >
                  Try a different file
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500 truncate">
            {stage === "success"
              ? "Data is live in BigQuery"
              : file
              ? file.name
              : "No file selected"}
          </p>
          {stage !== "success" && (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
            >
              <Upload size={14} />
              Upload
            </button>
          )}
        </div>

      </div>
    </>
  );

  return typeof document !== "undefined" ? createPortal(drawer, document.body) : null;
}